import { adRewardData, PROOF_LIMITS, type AdClaimResponse, type AdStatusResponse } from "@nano/contracts";
import { defaultGameConfig, firstClearGold, type HouseId } from "@nano/game-config";
import { adsVi } from "../i18n/ads.vi.ts";
import { friendlyError } from "../i18n/vi.ts";
import { watchRewarded, type WatchResult } from "../services/ads/ads.ts";
import { ApiClientError } from "../services/api/client.ts";
import { play } from "../services/audio/sfx.ts";
import { vibrate } from "../services/haptics.ts";
import { puzzleCommand, requireSync } from "./actions.ts";
import { appStore, setUi, toast } from "./store.ts";

/**
 * What a rewarded ad pays for, always by the player's choice:
 *  - out of free hints in a level: one more hint (paid on the device, no gold moves)
 *  - first clear of a level: its first-clear gold once more
 *  - the city gift chest: a little gold, a few times a day
 * Gold rewards are paid by the Worker only after Google's signed callback,
 * so a claim can wait a few seconds, or until the next visit to town.
 */
export const ADS = defaultGameConfig.economy.ads;

const PENDING_KEY = "nano.adClaims";
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Watched ads whose gold has not been paid yet (Google's callback or the level's sync still on the way). */
function pendingClaims(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string").slice(-20) : [];
  } catch {
    return [];
  }
}

function storeClaims(claims: readonly string[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(claims));
  } catch {
    // Private mode: the claim is retried in this session only.
  }
}

const rememberClaim = (data: string) => storeClaims([...pendingClaims().filter((entry) => entry !== data), data]);
const forgetClaim = (data: string) => storeClaims(pendingClaims().filter((entry) => entry !== data));

function sayWhyNot(result: Exclude<WatchResult, "rewarded">): void {
  toast(result === "skipped" ? adsVi.skipped : appStore.get().ui.online ? adsVi.unavailable : adsVi.needNetwork);
}

/** Asks the Worker a few times while Google's callback lands; null means it is still on the way. */
async function claimPatiently(data: string, attempts: number): Promise<AdClaimResponse | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await sleep(1200 + attempt * 800);
    try {
      const response = await requireSync().claimAd(data);
      if (response.status !== "waiting") {
        forgetClaim(data);
        return response;
      }
    } catch (error) {
      // A refused reward will never pay: let it go. Network trouble: keep it for later.
      if (error instanceof ApiClientError && !error.transient) forgetClaim(data);
      return null;
    }
  }
  return null;
}

let retrying = false;

/** Back in town or at start-up: collect any gold a watched ad still owes. */
export async function retryAdClaims(): Promise<void> {
  if (retrying || !appStore.get().ui.online) return;
  const claims = pendingClaims();
  if (claims.length === 0) return;
  retrying = true;
  try {
    for (const data of claims) {
      const response = await claimPatiently(data, 1);
      if (response?.status === "granted") {
        play("coin");
        toast(adsVi.double.got(response.goldAwarded));
      }
    }
  } finally {
    retrying = false;
  }
}

/** Out of hints: room for one more ad hint inside the proof's hint ceiling. */
export function canWatchForHint(): boolean {
  const used = appStore.get().ui.hud?.hints ?? 0;
  return used + ADS.hintsPerAd <= PROOF_LIMITS.maxHints;
}

/** One ad, one more hint for this attempt, shown right away. */
export async function watchAdForHint(): Promise<boolean> {
  const result = await watchRewarded("hint");
  if (result !== "rewarded") {
    sayWhyNot(result);
    return false;
  }
  setUi((ui) => ({ paidHints: ui.paidHints + ADS.hintsPerAd }));
  vibrate([10, 30, 10]);
  toast(adsVi.hint.got);
  puzzleCommand("hint");
  return true;
}

/** Gold the result card offers for a first clear. */
export function doubleGold(levelId: number): number {
  return firstClearGold(defaultGameConfig.economy, levelId);
}

export type DoubleOutcome = "granted" | "later" | "already" | "failed";

/** Result card: one ad pays the level's first-clear gold once more. */
export async function watchAdForDouble(houseId: HouseId, levelId: number): Promise<DoubleOutcome> {
  const userId = await requireSync().adIdentity();
  if (!userId) {
    toast(adsVi.needNetwork);
    return "failed";
  }
  const data = adRewardData({ kind: "double", nonce: crypto.randomUUID(), houseId, levelId });
  const result = await watchRewarded("gold", { userId, customData: data });
  if (result !== "rewarded") {
    sayWhyNot(result);
    return "failed";
  }
  rememberClaim(data);
  const response = await claimPatiently(data, 6);
  if (!response) {
    toast(adsVi.double.later);
    return "later";
  }
  if (response.status !== "granted") {
    toast(adsVi.double.already);
    return "already";
  }
  play("coin");
  vibrate([10, 30, 10, 30, 20]);
  toast(adsVi.double.got(response.goldAwarded));
  return "granted";
}

/** How many chests are left today (asks the Worker, which keeps the count). */
export async function giftStatus(): Promise<AdStatusResponse | null> {
  try {
    return await requireSync().adStatus();
  } catch {
    return null;
  }
}

export type GiftOutcome = { readonly gold: number; readonly left: number } | "later" | "empty" | "failed";

/** City chest: one ad, a handful of gold. */
export async function openGiftChest(): Promise<GiftOutcome> {
  const userId = await requireSync().adIdentity();
  if (!userId) {
    toast(adsVi.needNetwork);
    return "failed";
  }
  const data = adRewardData({ kind: "gift", nonce: crypto.randomUUID() });
  const result = await watchRewarded("gold", { userId, customData: data });
  if (result !== "rewarded") {
    sayWhyNot(result);
    return "failed";
  }
  rememberClaim(data);
  const response = await claimPatiently(data, 6);
  if (!response) return "later";
  if (response.status !== "granted") return "empty";
  play("coin");
  vibrate([10, 30, 10, 30, 20]);
  return { gold: response.goldAwarded, left: response.giftsLeftToday };
}

/** Settings: erase the server profile and start this device over (Google Play data deletion). */
export async function deleteMyData(): Promise<boolean> {
  try {
    await requireSync().deleteEverything();
  } catch (error) {
    const code = error instanceof ApiClientError ? error.code : "NETWORK";
    toast(code === "NETWORK" || code === "TIMEOUT" ? adsVi.privacy.deleteNeedNetwork : friendlyError(code));
    return false;
  }
  storeClaims([]);
  window.location.reload();
  return true;
}
